import React, {
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import format from 'string-template';
import { Typography } from '@mui/material';

import { DateFormat } from '../../../utils/date';
import WeeklyCheckin from '../../Model/WeeklyCheckin';
import LineChart from '../../pages/Analytics/components/LineChart';
import { WeightUnit } from '../CheckInStats/units';
import { getLbsFromKg, getKgFromLbs } from '../../../utils/measurement';
import { round } from '../../../utils/number';
import LoadingOverlay from '../LoadingOverlay';

import texts from './texts.json';
import { TooltipContainer, Text } from './styles';

const CLIENT_WEIGHT_KEY = 'Client Weight';

const ClientWeightChart = ({
  clientId,
  coachId,
  checkinDocs,
}) => {
  const [checkinData, setCheckinData] = useState([]);
  const [weightUnitText, setWeightUnitText] = useState('');
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const init = async () => {
      let checkins = [];
      if (checkinDocs) {
        checkins = checkinDocs;
      } else if (clientId && coachId) {
        const checkinCollection = await WeeklyCheckin.getByUserAndCoach(clientId, coachId);
        if (checkinCollection.hasDocs) {
          checkins = checkinCollection.docs.reverse();
        }
      }

      const unit = checkins[checkins.length - 1]?.checkInData?.current?.weightUnitType;
      const unitText = unit === WeightUnit.POUNDS ? texts.pounds : texts.kilograms;

      // Map the checkins to checkin data with date and weight information and enrich with previous weight
      const enrichedCheckins = checkins.reduce((acc, checkin) => {
        const weightValue = Number(checkin?.checkInData?.current?.weight?.value);
        const weightUnit = checkin?.checkInData?.current?.weightUnitType;
        let weight;
        // Convert weight value to the unit of the last checkin
        if (weightUnit === unit) {
          weight = weightValue;
        } else if (unit === WeightUnit.POUNDS) {
          weight = getLbsFromKg(weightValue);
        } else {
          weight = getKgFromLbs(weightValue);
        }

        // filter out checkins with no weight data
        if (weight) {
          const date = moment(checkin.submittedAt, DateFormat.DEFAULT_DATE_FORMAT)
            .format(DateFormat.DATE_MONTH_SHORT_DISPLAY_FORMAT);
          const formattedWeight = round(weight, 1);
          acc.push({
            name: date,
            [CLIENT_WEIGHT_KEY]: formattedWeight,
            date: checkin.submittedAt,
            previousWeight: acc.length > 0 ? acc[acc.length - 1][CLIENT_WEIGHT_KEY] : undefined,
          });
        }

        return acc;
      }, []);

      setCheckinData(enrichedCheckins);
      setWeightUnitText(unitText);
      setIsReady(true);
    };

    if (clientId || checkinDocs) {
      init();
    }
  }, [clientId, coachId, checkinDocs]);

  const customTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const { date, [CLIENT_WEIGHT_KEY]: currentWeight, previousWeight } = payload[0].payload;

      const difference = typeof previousWeight === 'number'
        ? round(currentWeight - previousWeight, 1)
        : null;
      const isPositive = difference > 0;

      return (
        <TooltipContainer>
          <Text>{moment(date).format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT)}</Text>
          <Text>{format(texts.weight, { currentWeight, unit: weightUnitText })}</Text>
          {difference ? (
            <Text $isPositive={isPositive} $isNegative={!isPositive}>
              {format(texts.change, { difference, unit: weightUnitText })}
            </Text>
          ) : (
            <Text $isNeutral>{texts.noChange}</Text>
          )}
        </TooltipContainer>
      );
    }

    return null;
  };

  if (checkinData.length === 0 && isReady) {
    return <Typography>{texts.noData}</Typography>;
  }

  return (
    <>
      <LineChart
        data={checkinData}
        keys={[CLIENT_WEIGHT_KEY]}
        showDots={checkinData.length < 12} // Dots on lots of data will only show the dots instead of a line
        CustomTooltip={customTooltip}
        xAxisProps={{
          padding: { left: 30, right: 30 },
        }}
        yAxisProps={{
          unit: ` ${weightUnitText}`,
          domain: [(dataMin) => Math.floor(dataMin) - 10, (dataMax) => Math.ceil(dataMax) + 10],
        }}
      />
      <LoadingOverlay isLoading={!isReady} />
    </>
  );
};

ClientWeightChart.propTypes = {
  clientId: PropTypes.string,
  coachId: PropTypes.string,
  checkinDocs: PropTypes.arrayOf(PropTypes.instanceOf(WeeklyCheckin)),
};

ClientWeightChart.defaultProps = {
  clientId: null,
  coachId: null,
  checkinDocs: null,
};

export default ClientWeightChart;
